import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';
import { isValidChinaPhone, normalizePhoneNumber, resolveCooldownSeconds } from '../utils/authFormUtils';

const DELETED_DATA_ITEMS = [
  '学习素材、知识颗粒及关联内容',
  '复习记录、学习足迹、学习目标与序列',
  '用户偏好、画像与学习设置',
  '收藏、标注与反馈记录',
  '账单、订单与资源包使用记录',
  '登录会话与第三方账号绑定',
];

function maskPhone(phone: string): string {
  const normalized = normalizePhoneNumber(phone);
  if (normalized.length !== 11) return phone;
  return `${normalized.slice(0, 3)}****${normalized.slice(-4)}`;
}

export function DeleteAccountPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const initialPhone = useMemo(() => normalizePhoneNumber(user?.phone || ''), [user?.phone]);
  const phoneLocked = initialPhone.length === 11;

  const [phone, setPhone] = useState(initialPhone);
  const [authCode, setAuthCode] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [codeCooldown, setCodeCooldown] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (codeCooldown <= 0) return;
    const timer = setInterval(() => setCodeCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(timer);
  }, [codeCooldown]);

  const normalizedPhone = normalizePhoneNumber(phone);

  async function handleSendCode() {
    setError(null);
    setMessage(null);

    if (!isValidChinaPhone(normalizedPhone)) {
      setError('请输入有效的手机号');
      return;
    }

    if (phoneLocked && normalizedPhone !== initialPhone) {
      setError('手机号与当前账户不匹配');
      return;
    }

    setSendingCode(true);
    try {
      const response = await authService.requestDeleteAccountSendCode(normalizedPhone);
      setMessage(response.message);
      setCodeCooldown(resolveCooldownSeconds(response.expiresAt));
    } catch (err) {
      setError(err instanceof Error ? err.message : '发送验证码失败');
    } finally {
      setSendingCode(false);
    }
  }

  async function handleDelete(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setMessage(null);

    if (!isValidChinaPhone(normalizedPhone)) {
      setError('请输入有效的手机号');
      return;
    }

    if (authCode.trim().length !== 6) {
      setError('请输入 6 位短信验证码');
      return;
    }

    if (confirmation !== 'DELETE_MY_ACCOUNT') {
      setError('请在确认框中输入 DELETE_MY_ACCOUNT');
      return;
    }

    if (!window.confirm('注销后所有私人数据将被永久删除且无法恢复，确定继续？')) {
      return;
    }

    setDeleting(true);
    try {
      await authService.deleteAccount(authCode.trim(), normalizedPhone);
      await signOut();
      navigate('/welcome', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : '注销失败');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <Link to="/profile" className="text-sm text-primary">← 账户</Link>
      <h1 className="text-2xl font-semibold text-ink">注销账号</h1>

      <div className="rounded-xl border border-danger/20 bg-danger/5 p-5 text-sm text-ink">
        <p className="font-medium text-danger">注销将永久删除以下私人数据，且不可恢复：</p>
        <ul className="mt-3 list-inside list-disc space-y-1 text-muted">
          {DELETED_DATA_ITEMS.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>

      <form onSubmit={(e) => void handleDelete(e)} className="space-y-4 rounded-xl border border-border bg-surface p-5">
        <p className="text-sm text-muted">
          验证码将发送至您账户绑定的手机号。请确认号码无误后再获取验证码。
        </p>

        <div>
          <label className="mb-1 block text-sm text-muted">手机号</label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="请输入手机号"
            disabled={phoneLocked}
            className="w-full rounded-lg border border-border px-3 py-2 disabled:bg-canvas"
          />
          {phoneLocked ? (
            <p className="mt-1 text-xs text-muted">已自动带入当前账户手机号（{maskPhone(initialPhone)}）</p>
          ) : null}
        </div>

        <div>
          <label className="mb-1 block text-sm text-muted">短信验证码</label>
          <div className="flex gap-2">
            <input
              value={authCode}
              onChange={(e) => setAuthCode(e.target.value)}
              placeholder="6 位验证码"
              maxLength={6}
              className="flex-1 rounded-lg border border-border px-3 py-2"
            />
            <button
              type="button"
              disabled={sendingCode || codeCooldown > 0}
              onClick={() => void handleSendCode()}
              className="shrink-0 rounded-lg border border-border px-4 py-2 text-sm disabled:opacity-50"
            >
              {sendingCode ? '发送中…' : codeCooldown > 0 ? `${codeCooldown}s` : '获取验证码'}
            </button>
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm text-muted">确认注销（输入 DELETE_MY_ACCOUNT）</label>
          <input
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder="DELETE_MY_ACCOUNT"
            className="w-full rounded-lg border border-border px-3 py-2 font-mono text-sm"
          />
        </div>

        {error ? <p className="text-sm text-danger">{error}</p> : null}
        {message ? <p className="text-sm text-muted">{message}</p> : null}

        <button
          type="submit"
          disabled={deleting}
          className="w-full rounded-lg bg-danger py-2 text-sm text-white disabled:opacity-50"
        >
          {deleting ? '注销中…' : '确认注销账号'}
        </button>
      </form>
    </div>
  );
}
